/* eslint-disable no-restricted-imports */
import { ChangeEvent, useRef } from 'react';
import { Field } from 'react-final-form';
import { ErrorField } from '../../../../routing/business/PartnerRegistration/types';
import { useTranslation } from '../../../../utils/useTranslation';
import { useMaskedInput } from '../../../hooks/useMaskedInput';
import { FormControl } from '../../../input/FormControl';
import { Input } from '../../../input/Input';
import { Flex } from '../../../layout/Flex';
import { getFieldError } from '../fieldMapping';
import { useBankData } from '../hooks/useBankData';
import { FieldProps } from '../types';
import { DropdownField } from './DropdownField';

interface PartnerRegistrationFromFieldsProps {
    fields: FieldProps[];
    errors?: ErrorField[];
}

export const PartnerRegistrationFromFields = ({
    fields,
    errors,
}: PartnerRegistrationFromFieldsProps) => {
    const t = useTranslation();
    const { handleBikChange } = useBankData();
    const bankNameOnChange = useRef<(value: string) => void>();

    return (
        <Flex direction="column" width="100%" pt={1.5}>
            {fields.map((props) => {
                if (props.type === 'text' || props.type === 'date') {
                    const {
                        name,
                        label,
                        placeholder,
                        required,
                        validate,
                        disabled,
                        formatter,
                        valueExtractor,
                    } = props;

                    return (
                        <Field
                            key={name}
                            name={name}
                            validate={validate}
                            render={({ input, meta }) => {
                                if (name === 'bankName') {
                                    bankNameOnChange.current = input.onChange;
                                }

                                const maskedInput = formatter
                                    ? useMaskedInput(
                                          formatter,
                                          input.value,
                                          input.onChange,
                                          valueExtractor,
                                      )
                                    : null;

                                const onChange = (event: ChangeEvent<HTMLInputElement>) => {
                                    if (name === 'bik') {
                                        handleBikChange(
                                            event.target.value,
                                            bankNameOnChange?.current,
                                        );
                                    }

                                    if (maskedInput) {
                                        maskedInput.onChange(event);
                                    } else {
                                        input.onChange(event);
                                    }
                                };

                                const fieldError = errors ? getFieldError(errors, name) : undefined;
                                const hasError =
                                    (meta.touched && Boolean(meta.error)) || Boolean(fieldError);
                                const errorMessage =
                                    fieldError || (meta.touched ? meta.error : undefined);

                                return (
                                    <FormControl
                                        pt={1}
                                        pb={1}
                                        label={t(label)}
                                        required={required}
                                        disabled={disabled}
                                        error={hasError}
                                        message={errorMessage}
                                    >
                                        <Input
                                            {...input}
                                            value={maskedInput ? maskedInput.value : input.value}
                                            type={props.type}
                                            autoComplete="off"
                                            placeholder={t(placeholder)}
                                            onBlur={(event) => {
                                                input.onBlur(event);
                                            }}
                                            disabled={disabled}
                                            onChange={onChange}
                                        />
                                    </FormControl>
                                );
                            }}
                        />
                    );
                }

                if (props.type === 'dropdown') {
                    return <DropdownField key={props.name} {...props} />;
                }

                return null;
            })}
        </Flex>
    );
};
